import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, SideSheet, useToast } from '@digitalisation/enlixy-comp';
import { useUnifiedAuth } from '../../auth/useUnifiedAuth';
import type { Machine } from '../../services/BackendDataService';
import { isDemoMode } from '../../services/demoMode';
import { resolveAll, type Kpi, type KpiScope, type ResolvedTarget } from '../../services/KpiTargetService';
import { useSAPDataStore } from '../../store/SAPDataStore';
import { KpiTargetFields } from './KpiTargetFields';
import { parseInputs, toInputs, type TargetInputs } from './kpiTargetForm';

/**
 * "Targets" on the machine page: the targets of this machine and of all
 * machines of its type. The line's targets are set in the line's KPI
 * settings; empty fields show what applies instead.
 */
export function MachineTargetsButton({ machine }: { machine?: Machine }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!machine) return null;
  return (
    <>
      <Button size="sm" variant="secondary" icon="rules" onClick={() => setOpen(true)} data-tour="machine-targets">
        {t('kpiTargets.button')}
      </Button>
      <SideSheet
        open={open}
        onClose={() => setOpen(false)}
        title={t('kpiTargets.title')}
        subtitle={t('kpiTargets.subtitle')}
        width={560}
      >
        {open && <MachineTargetsForm machine={machine} />}
      </SideSheet>
    </>
  );
}

function MachineTargetsForm({ machine }: { machine: Machine }) {
  const { t } = useTranslation();
  const { token } = useUnifiedAuth();
  const index = useSAPDataStore((s) => s.kpiTargetIndex);
  const fetchKpiTargets = useSAPDataStore((s) => s.fetchKpiTargets);
  // the targets are loaded once per session: read them again when the sheet
  // opens, so the fields show what others saved since
  useEffect(() => {
    if (token) void fetchKpiTargets(token);
  }, [token, fetchKpiTargets]);
  const lines = useSAPDataStore((s) => s.lines);
  const sn = machine.maschinennummer || machine.maschineBK;
  const type = machine.bautyp || machine.machineType || '';
  const line = lines.find((l) => l.linieBK === machine.lineId);
  const lineName = line ? [line.linieNummer, line.linieName].filter((x) => x && x !== 'undefined').join(' - ') : machine.lineId;

  // what applies when the machine's own field is empty: its line, then its type
  const fromLineAndType = useMemo(
    () => resolveAll(index, { lineBk: machine.lineId, machineType: type }),
    [index, machine.lineId, type],
  );

  return (
    <div className="space-y-6 px-5 py-4">
      <TargetLevel
        scope="machine"
        scopeId={sn}
        title={t('kpiTargets.machine', { id: sn })}
        inherited={fromLineAndType}
      />
      <p className="rounded-xl bg-surface-2 px-3 py-2 text-[13px] text-fg-2 ring-1 ring-line/60">
        {t('kpiTargets.lineNote', { line: lineName || '–' })}
      </p>
      {type && <TargetLevel scope="machine_type" scopeId={type} title={t('kpiTargets.machineType', { type })} />}
      <p className="text-xs text-muted">{t('kpiTargets.hint')}</p>
    </div>
  );
}

/** One level's fields with its own Save button. */
export function TargetLevel({
  scope,
  scopeId,
  title,
  inherited,
}: {
  scope: KpiScope;
  scopeId: string;
  title: string;
  inherited?: Partial<Record<Kpi, ResolvedTarget>>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { token } = useUnifiedAuth();
  const row = useSAPDataStore((s) => s.kpiTargetIndex.get(`${scope}:${scopeId}`));
  const kpiTargetsLoaded = useSAPDataStore((s) => s.kpiTargetsLoaded);
  const saveKpiTarget = useSAPDataStore((s) => s.saveKpiTarget);
  const [inputs, setInputs] = useState<TargetInputs>(() => toInputs(row?.targets));
  const [invalid, setInvalid] = useState<Kpi | null>(null);
  const [saving, setSaving] = useState(false);
  const demo = isDemoMode();
  useEffect(() => setInputs(toInputs(row?.targets)), [row]);

  const save = async () => {
    const parsed = parseInputs(inputs);
    if ('invalid' in parsed) {
      setInvalid(parsed.invalid);
      toast.error(new Error(t('kpiTargets.invalid')), t('kpiTargets.saveFailed'));
      return;
    }
    setInvalid(null);
    setSaving(true);
    try {
      if (!token) throw new Error('Not signed in');
      // without loaded targets a save would replace a row nobody saw
      if (!kpiTargetsLoaded) throw new Error(t('kpiTargets.notLoaded'));
      await saveKpiTarget(scope, scopeId, parsed.targets, token);
      toast.success(t('kpiTargets.saved'));
    } catch (error) {
      toast.error(error, t('kpiTargets.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-fg">{title}</h3>
        {row?.updatedBy && <span className="text-[11px] text-muted">{t('kpiTargets.changedBy', { user: row.updatedBy })}</span>}
      </div>
      <KpiTargetFields
        id={`${scope}-${scopeId}`}
        inputs={inputs}
        onChange={(kpi, value) => setInputs((v) => ({ ...v, [kpi]: value }))}
        inherited={inherited}
        invalid={invalid}
        disabled={demo || saving}
        hints={{ output: t('kpiTargets.outputHintMachine') }}
      />
      <div className="flex items-center justify-end gap-3">
        {demo && <span className="text-xs text-muted">{t('kpiTargets.demo')}</span>}
        <Button size="sm" variant="primary" onClick={save} busy={saving} disabled={demo}>
          {t('kpiTargets.save')}
        </Button>
      </div>
    </section>
  );
}
